import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bidAPI } from '../services/api';
import './BidsReport.css';

const BidsReport = () => {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'sold'
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/');
      return;
    }
    loadBids();
  }, []);

  const loadBids = async () => {
    try {
      const response = await bidAPI.getAllBidsReport();
      setBids(response.data);
    } catch (error) {
      console.error('Error loading bids:', error);
      alert('Error al cargar el reporte de ofertas');
    } finally {
      setLoading(false);
    }
  };

  const filteredBids = bids.filter(bid => {
    if (filter === 'active') return !bid.vendido;
    if (filter === 'sold') return bid.vendido;
    return true;
  });

  // Agrupar por teléfono
  const groupedBids = filteredBids.reduce((acc, bid) => {
    if (!acc[bid.phone_id]) {
      acc[bid.phone_id] = {
        phone: {
          id: bid.phone_id,
          marca: bid.marca,
          modelo: bid.modelo,
          imei: bid.imei,
          precio_base: bid.precio_base,
          vendido: bid.vendido
        },
        bids: []
      };
    }
    acc[bid.phone_id].bids.push(bid);
    return acc;
  }, {});

  const getWinner = (bids) => {
    if (bids.length === 0) return null;
    return bids.reduce((max, bid) => bid.amount > max.amount ? bid : max, bids[0]);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando reporte...</p>
      </div>
    );
  }

  return (
    <div className="bids-report-container">
      <header className="bids-report-header">
        <h1>📊 Reporte de Ofertas</h1>
        <div className="header-actions">
          <button onClick={() => navigate('/admin')} className="btn-back">⬅️ Admin</button>
          <button onClick={() => navigate('/')} className="btn-catalog">📱 Catálogo</button>
          <button onClick={() => { logout(); navigate('/login'); }} className="btn-logout">🚪 Salir</button>
        </div>
      </header>

      <div className="bids-report-content">
        <div className="report-toolbar">
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              📋 Todos ({bids.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
              onClick={() => setFilter('active')}
            >
              🔥 Activos ({bids.filter(b => !b.vendido).length})
            </button>
            <button 
              className={`filter-btn ${filter === 'sold' ? 'active' : ''}`}
              onClick={() => setFilter('sold')}
            >
              ✅ Vendidos ({bids.filter(b => b.vendido).length})
            </button>
          </div>
        </div>

        {Object.keys(groupedBids).length === 0 ? (
          <div className="no-bids-report">
            <h2>📭 No hay ofertas registradas</h2>
          </div>
        ) : (
          <div className="phones-with-bids">
            {Object.values(groupedBids).map(({ phone, bids }) => {
              const winner = getWinner(bids);
              return (
                <div key={phone.id} className="phone-bid-section">
                  <div className="phone-header">
                    <div className="phone-info-header">
                      <h3>
                        {phone.marca} {phone.modelo}
                        {phone.vendido && <span className="sold-label">✅ VENDIDO</span>}
                      </h3>
                      {phone.imei && <p className="imei-text">IMEI: {phone.imei}</p>}
                      <p className="base-price">
                        Precio Base: RD$ {parseFloat(phone.precio_base).toLocaleString()}
                      </p>
                    </div>
                    <div className="phone-stats">
                      <div className="stat-item">
                        <span className="stat-label">Total Ofertas:</span>
                        <span className="stat-value">{bids.length}</span>
                      </div>
                      {winner && (
                        <div className="stat-item winner-stat">
                          <span className="stat-label">🏆 Ganador:</span>
                          <span className="stat-value">{winner.username}</span>
                          <span className="winner-amount">
                            RD$ {parseFloat(winner.amount).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bids-table-wrapper">
                    <table className="bids-table">
                      <thead>
                        <tr>
                          <th>Usuario</th>
                          <th>Monto</th>
                          <th>Fecha</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bids
                          .sort((a, b) => b.amount - a.amount)
                          .map(bid => (
                            <tr key={bid.id} className={bid.id === winner?.id ? 'winner-row' : ''}>
                              <td>{bid.username}</td>
                              <td className="amount-cell">
                                RD$ {parseFloat(bid.amount).toLocaleString()}
                                {bid.id === winner?.id && <span className="winner-icon">🏆</span>}
                              </td>
                              <td>{new Date(bid.created_at).toLocaleString('es-DO')}</td>
                              <td>
                                {bid.id === winner?.id ? (
                                  <span className="status-winner">Ganador</span>
                                ) : (
                                  <span className="status-normal">Activo</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BidsReport;
